import { createLogger } from '../../shared/logger.js';
import { applyRemoteSnapshot } from './lotOperations';

const log = createLogger('portal:realtime');

interface RealTimeSubscriberDeps {
  cfg: any;
  state: any;
  ensureAccessToken: () => Promise<string | null>;
  mapRemoteDocument: (doc: any) => any;
  render: () => void;
  triggerPoll: () => Promise<void>;
}

export interface RealTimeSubscriber {
  connect: (reason?: string) => Promise<void>;
  disconnect: (reason?: string, permanent?: boolean) => void;
  attachVisibilityHandler: () => void;
}

function resolveStreamUrl(cfg: any): string | null {
  const url = cfg?.realTime?.scrapeStreamUrl || cfg?.realTime?.scrapeUrl;
  if (!url || typeof url !== 'string') {
    return null;
  }
  const trimmed = url.trim();
  return trimmed ? trimmed : null;
}

function parseScrapePayload(raw: string | null | undefined): {
  type?: string;
  userId?: string;
  docId?: string;
  document?: any;
} | null {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as any;
  } catch {
    return null;
  }
}

export function createRealTimeSubscriber(deps: RealTimeSubscriberDeps): RealTimeSubscriber {
  const { cfg, state, ensureAccessToken, mapRemoteDocument, render, triggerPoll } = deps;

  let source: EventSource | null = null;
  let reconnectTimer: number | null = null;
  let visibilityHandlerAttached = false;
  let shouldReconnect = true;
  let connecting = false;

  function clearReconnectTimer() {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function scheduleReconnect() {
    clearReconnectTimer();
    if (!shouldReconnect) return;
    reconnectTimer = window.setTimeout(() => {
      void connect('reconnect');
    }, 5000);
  }

  function disconnect(reason = 'manual', permanent = false) {
    if (permanent) {
      shouldReconnect = false;
    }
    clearReconnectTimer();
    if (source) {
      try {
        source.close();
      } catch {
        /* noop */
      }
    }
    source = null;
    connecting = false;
    log.info('[realtime] disconnected', { reason, permanent });
  }

  function attachVisibilityHandler() {
    if (visibilityHandlerAttached) return;
    visibilityHandlerAttached = true;
    document.addEventListener('visibilitychange', () => {
      const hidden = document.visibilityState === 'hidden';
      if (hidden) {
        disconnect('hidden');
      } else if (shouldReconnect) {
        void connect('visible');
      }
    });
  }

  function handleScrapeMessage(raw: string | null) {
    const data = parseScrapePayload(raw);
    if (!data) return;
    if (data.type && data.type !== 'scrape') return;
    const eventUserId = data.userId;
    const currentUserId = state?.user?.sub;
    if (eventUserId && currentUserId && eventUserId !== currentUserId) return;

    if (data.document) {
      const mapped = mapRemoteDocument(data.document);
      if (mapped) {
        const lots = Array.isArray(state.lots) ? state.lots : [];
        const idx = lots.findIndex((lot: any) => lot && lot.id === mapped.id);
        if (idx >= 0) {
          lots[idx] = mapped;
        } else {
          lots.unshift(mapped);
        }
        state.lots = lots;
        applyRemoteSnapshot(lots);
        render();
        return;
      }
    }

    void triggerPoll();
  }

  async function connect(reason = 'manual'): Promise<void> {
    if (connecting || source) return;
    if (!shouldReconnect) return;
    if (state.mode !== 'auth0') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const streamUrl = resolveStreamUrl(cfg);
    if (!streamUrl) return;

    if (typeof EventSource === 'undefined') {
      log.warn('[realtime] EventSource not available in this environment');
      shouldReconnect = false;
      return;
    }

    connecting = true;
    try {
      const token = await ensureAccessToken();
      if (!token) {
        log.warn('[realtime] no access token available; will retry later');
        connecting = false;
        scheduleReconnect();
        return;
      }

      const url = new URL(streamUrl);
      url.searchParams.set('access_token', token);
      if (state.user?.sub) {
        url.searchParams.set('userId', state.user.sub);
      }
      url.searchParams.set('t', Date.now().toString());

      source = new EventSource(url.toString());
      source.onopen = () => {
        connecting = false;
        log.info('[realtime] connected', { reason });
      };
      source.onerror = err => {
        log.warn('[realtime] connection error', err);
        connecting = false;
        disconnect('error');
        scheduleReconnect();
      };
      source.onmessage = event => handleScrapeMessage(event?.data);
      source.addEventListener('scrape', event => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleScrapeMessage((event as MessageEvent<any>)?.data);
      });
    } catch (error) {
      log.warn('[realtime] failed to connect', error);
      connecting = false;
      scheduleReconnect();
    }
  }

  return {
    connect,
    disconnect,
    attachVisibilityHandler,
  };
}
